import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { watchAPI, imageAPI, getServerURL } from '../services/api';
import { ArrowLeft, Upload, Trash2, Edit, EyeOff, Globe, FileUp, Share2, Copy, Check } from 'lucide-react';
import ImageCarousel from '../components/ImageCarousel';
import DocumentViewer from '../components/DocumentViewer';

const WatchDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [watch, setWatch] = useState(null);
  const [images, setImages] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      const watchResponse = await watchAPI.getOne(id);
      setWatch(watchResponse.data);

      const imagesResponse = await imageAPI.getAll(id);
      const imageArray = Array.isArray(imagesResponse.data.images) ? imagesResponse.data.images : [];
      setImages(imageArray);

      // Load documents from watch data
      setDocuments(Array.isArray(watchResponse.data.documents) ? watchResponse.data.documents : []);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Watch not found');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;

    // Validate files
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'];
    const maxSize = 20 * 1024 * 1024; // 20MB

    const invalidFiles = [];
    Array.from(files).forEach(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      if (!allowedExtensions.includes(ext)) {
        invalidFiles.push(`${file.name} (unsupported format)`);
      } else if (file.size > maxSize) {
        invalidFiles.push(`${file.name} (too large, max 20MB)`);
      }
    });

    if (invalidFiles.length > 0) {
      setError(`Cannot upload the following files:\n${invalidFiles.join('\n')}\n\nAllowed formats: JPG, PNG, WEBP, HEIC`);
      return;
    }

    setUploadingImages(true);
    setError('');
    try {
      await imageAPI.upload(id, Array.from(files));
      const response = await imageAPI.getAll(id);
      setImages(Array.isArray(response.data.images) ? response.data.images : []);
      setSuccessMessage(`Successfully uploaded ${files.length} image(s)`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('[Upload] Error details:', err);
      console.error('[Upload] Error response:', err.response);

      let errorMessage = 'Failed to upload images';
      if (err.response?.data?.detail) {
        errorMessage = err.response?.data?.detail;
      } else if (err.message) {
        errorMessage = `Upload failed: ${err.message}`;
      }

      setError(errorMessage);
    } finally {
      setUploadingImages(false);
    }
  };

  const handleDeleteImage = async (filename) => {
    // Confirmation is handled in ImageCarousel component
    try {
      setError('');
      await imageAPI.delete(id, filename);
      const response = await imageAPI.getAll(id);
      setImages(Array.isArray(response.data.images) ? response.data.images : []);
      setSuccessMessage('Image deleted successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting image:', err);
      setError('Failed to delete image');
    }
  };

  const handleDeleteWatch = async () => {
    if (window.confirm('Are you sure you want to delete this watch? This action cannot be undone.')) {
      try {
        setError('');
        await watchAPI.delete(id);
        navigate('/inventory');
      } catch (err) {
        console.error('Error deleting watch:', err);
        setError('Failed to delete watch');
      }
    }
  };

  const handleTogglePublic = async () => {
    try {
      setError('');
      await watchAPI.update(id, {
        is_public: !watch.is_public
      });
      setWatch(prev => ({ ...prev, is_public: !prev.is_public }));
      setSuccessMessage('Visibility updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error toggling visibility:', err);
      setError('Failed to update visibility');
    }
  };

  const handleDocumentUpload = async (files, documentType = 'certificate') => {
    if (!files || files.length === 0) return;

    setUploadingDocuments(true);
    setError('');
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('files', file);
      });
      formData.append('document_type', documentType);

      const token = localStorage.getItem('token');
      const response = await fetch(`${getServerURL()}/api/images/watches/${id}/documents`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) throw new Error('Upload failed');

      const data = await response.json();
      setDocuments(data.documents);
      setSuccessMessage(`Successfully uploaded ${files.length} document(s)`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error uploading documents:', err);
      setError('Failed to upload documents');
    } finally {
      setUploadingDocuments(false);
    }
  };

  const handleDeleteDocument = async (index) => {
    if (window.confirm('Delete this document?')) {
      try {
        setError('');
        const token = localStorage.getItem('token');
        const response = await fetch(`${getServerURL()}/api/images/watches/${id}/documents/${index}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) throw new Error('Delete failed');

        setDocuments(prev => prev.filter((_, i) => i !== index));
        setSuccessMessage('Document deleted successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        console.error('Error deleting document:', err);
        setError('Failed to delete document');
      }
    }
  };

  const calculateMargin = (cost, price) => {
    return (((price - cost) / price) * 100).toFixed(1);
  };

  const calculateProfit = (cost, price) => {
    return price - cost;
  };

  const handleCopyPublicLink = () => {
    const publicLink = `${window.location.origin}/watch/${id}`;
    navigator.clipboard.writeText(publicLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-900 border-t-transparent"></div>
      </div>
    );
  }

  if (error && !watch) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Error Loading Watch</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/inventory')}
            className="px-6 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-2xl font-semibold shadow-lg hover:from-slate-950 hover:to-slate-800 transition-all"
          >
            Back to Inventory
          </button>
        </div>
      </div>
    );
  }

  if (!watch) return null;

  return (
    <div className="space-y-6">
      {error && watch && (
        <div className="bg-red-50 border-2 border-red-200 text-red-800 px-4 py-3 rounded-xl">
          <p className="font-medium whitespace-pre-line">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border-2 border-green-200 text-green-800 px-4 py-3 rounded-xl">
          <p className="font-medium">{successMessage}</p>
        </div>
      )}

      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/inventory')}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Inventory
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={handleDeleteWatch}
            className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-2xl font-semibold shadow-lg hover:bg-red-700 transition-all"
          >
            <Trash2 className="w-5 h-5" />
            Delete
          </button>
          <button
            onClick={() => navigate(`/inventory/edit/${id}`)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-slate-900 to-slate-700 text-white rounded-2xl font-semibold shadow-lg hover:from-slate-950 hover:to-slate-800 transition-all"
          >
            <Edit className="w-5 h-5" />
            Edit Watch
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          {/* Image Carousel */}
          <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Images ({images.length})</h3>
              <label className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-xl cursor-pointer hover:bg-slate-800 transition-all">
                <Upload className="w-4 h-4" />
                Upload Images
                <input
                  type="file"
                  multiple
                  accept="image/*,image/heic,image/heif,.heic,.heif"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e.target.files)}
                  disabled={uploadingImages}
                />
              </label>
            </div>
            <ImageCarousel
              images={images}
              alt={`${watch.brand} ${watch.model}`}
              onDelete={handleDeleteImage}
            />
            {uploadingImages && (
              <div className="text-center py-4 mt-4">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-900 border-t-transparent mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Uploading images...</p>
              </div>
            )}
          </div>

          {/* Documents */}
          <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">Documents ({documents.length})</h3>
              <label className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-xl cursor-pointer hover:bg-amber-700 transition-all">
                <FileUp className="w-4 h-4" />
                Upload PDF
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  onChange={(e) => handleDocumentUpload(e.target.files)}
                  disabled={uploadingDocuments}
                />
              </label>
            </div>
            <DocumentViewer
              documents={documents}
              onDelete={handleDeleteDocument}
            />
            {uploadingDocuments && (
              <div className="text-center py-4 mt-4">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-amber-600 border-t-transparent mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Uploading documents...</p>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
            <div className="mb-6">
              <h1 className="text-4xl font-bold text-gray-900 mb-2">{watch.brand}</h1>
              <h2 className="text-2xl text-gray-600 font-medium">{watch.model}</h2>
              {watch.reference && (
                <p className="text-gray-500 mt-2">Ref: {watch.reference}</p>
              )}
            </div>

            <div className={`inline-block px-4 py-2 rounded-full text-sm font-bold mb-6 ${
              watch.status === 'Available'
                ? 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700'
                : watch.status === 'Reserved'
                ? 'bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-700'
                : 'bg-gradient-to-r from-gray-100 to-slate-100 text-gray-700'
            }`}>
              {watch.status}
            </div>

            {/* Visibility Toggle */}
            <div className="mb-6 p-4 bg-gradient-to-r from-slate-50 to-stone-50 rounded-xl border-2 border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {watch.is_public ? (
                    <Globe className="w-5 h-5 text-slate-900" />
                  ) : (
                    <EyeOff className="w-5 h-5 text-gray-600" />
                  )}
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      {watch.is_public ? 'Public Listing' : 'Private Listing'}
                    </div>
                    <p className="text-xs text-gray-600">
                      {watch.is_public
                        ? 'Visible to other dealers in the marketplace'
                        : 'Only visible to you in your inventory'}
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={watch.is_public}
                    onChange={handleTogglePublic}
                    className="sr-only peer"
                  />
                  <div className="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-slate-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-slate-900"></div>
                </label>
              </div>
            </div>

            {/* Shareable Link - Only show if public */}
            {watch.is_public && (
              <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-100">
                <div className="flex items-start gap-3 mb-3">
                  <Share2 className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900 mb-1">
                      Shareable Public Link
                    </div>
                    <p className="text-xs text-gray-600 mb-3">
                      Anyone with this link can view this watch listing
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={`${window.location.origin}/watch/${id}`}
                        readOnly
                        className="flex-1 px-3 py-2 bg-white border border-blue-200 rounded-lg text-sm text-gray-700 font-mono"
                      />
                      <button
                        onClick={handleCopyPublicLink}
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                          linkCopied
                            ? 'bg-green-600 text-white'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {linkCopied ? (
                          <>
                            <Check className="w-4 h-4" />
                            Copied!
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {watch.description && (
              <p className="text-gray-700 leading-relaxed mb-6">{watch.description}</p>
            )}

            <div className="grid grid-cols-2 gap-4">
              {watch.year && (
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <div className="text-sm text-gray-500 mb-1">Year</div>
                  <div className="font-bold text-gray-900">{watch.year}</div>
                </div>
              )}
              {watch.condition && (
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <div className="text-sm text-gray-500 mb-1">Condition</div>
                  <div className="font-bold text-gray-900">{watch.condition}</div>
                </div>
              )}
              {watch.case_size && (
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <div className="text-sm text-gray-500 mb-1">Case Size</div>
                  <div className="font-bold text-gray-900">{watch.case_size}</div>
                </div>
              )}
              {watch.case_material && (
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <div className="text-sm text-gray-500 mb-1">Material</div>
                  <div className="font-bold text-gray-900">{watch.case_material}</div>
                </div>
              )}
              {watch.movement && (
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <div className="text-sm text-gray-500 mb-1">Movement</div>
                  <div className="font-bold text-gray-900">{watch.movement}</div>
                </div>
              )}
              {watch.serial_number && (
                <div className="p-4 bg-gray-50 rounded-2xl">
                  <div className="text-sm text-gray-500 mb-1">Serial</div>
                  <div className="font-bold text-gray-900">{watch.serial_number}</div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">💰 Pricing</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-gradient-to-r from-gray-50 to-slate-50 rounded-2xl">
                <span className="font-medium text-gray-700">Cost</span>
                <span className="font-bold text-2xl text-gray-900">${parseFloat(watch.cost || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-gradient-to-r from-slate-50 to-stone-50 rounded-2xl">
                <span className="font-medium text-gray-700">Asking Price</span>
                <span className="font-bold text-3xl text-slate-900">${parseFloat(watch.asking_price || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl">
                <span className="font-medium text-gray-700">Potential Profit</span>
                <span className="font-bold text-2xl text-green-600">${calculateProfit(watch.cost, watch.asking_price).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl">
                <span className="font-medium text-gray-700">Profit Margin</span>
                <span className="font-bold text-2xl text-amber-600">{calculateMargin(watch.cost, watch.asking_price)}%</span>
              </div>
            </div>
          </div>

          {(watch.box || watch.papers) && (
            <div className="bg-white rounded-3xl shadow-xl p-8 border-2 border-gray-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">📦 Accessories</h3>
              <div className="grid grid-cols-2 gap-4">
                {watch.box && (
                  <div className="p-4 bg-blue-50 rounded-2xl">
                    <div className="text-sm text-gray-500 mb-1">Box</div>
                    <div className="font-bold text-gray-900">{watch.box}</div>
                  </div>
                )}
                {watch.papers && (
                  <div className="p-4 bg-blue-50 rounded-2xl">
                    <div className="text-sm text-gray-500 mb-1">Papers</div>
                    <div className="font-bold text-gray-900">{watch.papers}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WatchDetailPage;
